// src/pages/PengyouquanChat.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./PengyouquanChat.css";
import { apiFetch } from "../api";
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";

const MAX_MESSAGES_TO_SEND = 20;
const MAX_SAVED_MESSAGES = 100;
const DEFAULT_TEMPERATURE = 0.6;
const DEFAULT_TOP_P = 0.9;
const DEFAULT_MAX_TOKENS = 680;
const ONE_POST_HINT =
  "输出限制：仅输出 1 条朋友圈文案；不要使用序号、标题或说明；不要解释；直接给可发布的正文。";

function safeEnv(k) {
  try { if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env[k] != null) return import.meta.env[k]; } catch {}
  try { if (typeof process !== "undefined" && process.env && process.env[k] != null) return process.env[k]; } catch {}
  return undefined;
}

export default function PengyouquanChat({ systemPrompt = "你是朋友圈文案助手，擅长把需求快速改写为可发布的朋友圈内容。输出简洁、接地气，必要时补表情与话题。" }) {
  /** ---------------- 初始化通道配置 ---------------- */
  const [initReady, setInitReady] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [initError, setInitError] = useState("");
  const [serverModel, setServerModel] = useState("");
  const [serverApiKey, setServerApiKey] = useState("");

  const loadServerConfig = useCallback(async () => {
    setInitLoading(true);
    setInitError("");
    try {
      const data = await apiFetch("/api/pyq/302/config", { method: "GET" });
      if (!data?.model || !data?.apiKey) throw new Error("配置缺失");
      setServerModel(data.model);
      setServerApiKey(data.apiKey);
      setInitReady(true);
    } catch (e) {
      if (e?.status === 401 || /unauthorized/i.test(e?.message || "")) {
        setInitError("用户未登录，请先登录");
      } else {
        setInitError(e?.message || "初始化失败");
      }
      setInitReady(false);
    } finally {
      setInitLoading(false);
    }
  }, []);
  useEffect(() => { loadServerConfig(); }, [loadServerConfig]);

  const apiKey = useMemo(() => {
    return (
      serverApiKey ||
      safeEnv("VITE_302_API_KEY") ||
      safeEnv("REACT_APP_302_API_KEY") ||
      (typeof localStorage !== "undefined" ? localStorage.getItem("302_API_KEY") : "") ||
      ""
    );
  }, [serverApiKey]);
  const model = useMemo(() => serverModel || "", [serverModel]);

  /** ---------------- 对话状态 ---------------- */
  const [messages, setMessages] = useState(() => {
    const cache = localStorage.getItem("pyq_chat_messages_v2");
    if (cache) return JSON.parse(cache);
    return [
      { role: "system", content: systemPrompt },
      { role: "assistant", content: "嗨～ 先从『场景开头』里点一个，或直接输入，也能叠加快捷提示再发送。" },
    ];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [useStream, setUseStream] = useState(true);
  const [error, setError] = useState("");

  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // ✅ 新增：输入区折叠（阅读模式）
  const [inputCollapsed, setInputCollapsed] = useState(false);
  // ✅ 保留 & 实现：快捷区折叠
  const [shortcutsCollapsed, setShortcutsCollapsed] = useState(false);

  useEffect(() => {
    if (messages.length > MAX_SAVED_MESSAGES) {
      setMessages((prev) => prev.slice(-MAX_SAVED_MESSAGES));
      return;
    }
    localStorage.setItem("pyq_chat_messages_v2", JSON.stringify(messages));
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  /** ---------------- 快捷提示 ---------------- */
  const quickPrompts = [
    "人设型：受众xx，风格xx，请给1条朋友圈文案。",
    "需求型：我要招募xx学员，给1条落地执行版朋友圈。",
    "产品型：主打亮点xx，对象xx，给1条含行动号召的朋友圈。",
    "把下面这段粗稿润色成朋友圈长文案：xxx",
  ];
  const chipPrompts = [
    { label: "更短", text: "请把文案写得更短一些，信息更密集。" },
    { label: "更长", text: "请把文案写得更长一些，适度铺陈但不重复。" },
    { label: "换风格更温暖", text: "将风格调成更温暖、有人情味，口吻更亲近。" },
    { label: "改产品型", text: "请按产品型来写，突出核心卖点与用户收益。" },
    { label: "改需求型", text: "请按需求型来写，强调痛点、解决方案与行动号召。" },
    { label: "还是人设型", text: "按人设型写，体现个人视角与价值观。" },
    { label: "加书籍金句", text: "适当引用一本相关书籍中的一句金句作点睛。" },
    { label: "不要最后一句短句", text: "去掉最后那句短句收尾，换成平实自然的结尾。" },
    { label: "换一下开头风格", text: "把开头换一种风格，更抓人眼球。" },
  ];

  const [activeBase, setActiveBase] = useState(-1);
  const [activeChip, setActiveChip] = useState(-1);

  const onQuickSelect = (i) => {
    setActiveBase(i);
    setActiveChip(-1);
    setInput(quickPrompts[i]);
  };

  const onChipClick = (i) => {
    // 不叠加：替换上一次芯片文本
    const txt = chipPrompts[i].text;
    setInput((prev) => {
      let next = prev || "";
      if (activeChip >= 0) {
        const old = chipPrompts[activeChip].text;
        const withNL = "\n" + old;
        if (next.endsWith(old)) next = next.slice(0, -old.length).trimEnd();
        else if (next.endsWith(withNL)) next = next.slice(0, -withNL.length).trimEnd();
      }
      if (next && !/\s$/.test(next)) next += "\n";
      next += txt;
      return next;
    });
    setActiveChip(i);
  };

  /** ---------------- 请求封装 ---------------- */
  function buildWindowForRequest(all) {
    const sys = all.find((m) => m.role === "system");
    const valid = all.filter((m) => m.role !== "system" && typeof m.content === "string" && m.content.trim());
    const windowMsgs = valid.slice(-MAX_MESSAGES_TO_SEND);
    return sys ? [sys, ...windowMsgs] : windowMsgs;
  }
  function injectOnePostHint(msgs) {
    const hint = { role: "system", content: ONE_POST_HINT };
    return msgs[0]?.role === "system" ? [msgs[0], hint, ...msgs.slice(1)] : [hint, ...msgs];
  }
  function fmtHtml(text) {
    if (!text) return "";
    let t = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/`([^`]+?)`/g, "<code>$1</code>");
    return t.replace(/\n/g, "<br/>");
  }
  async function extractServerMessage(resp) {
    const ct = resp.headers.get("content-type") || "";
    try {
      if (ct.includes("application/json")) {
        const j = await resp.json();
        return j?.message || j?.error || j?.msg || JSON.stringify(j);
      }
      return (await resp.text()) || "请求失败";
    } catch {
      return "请求失败";
    }
  }

  const call302 = async (history, onStreamChunk) => {
    const endpoint = "/api/pyq/302/stream";
    const body = {
      model,
      messages: injectOnePostHint(buildWindowForRequest(history)),
      temperature: DEFAULT_TEMPERATURE,
      top_p: DEFAULT_TOP_P,
      max_tokens: DEFAULT_MAX_TOKENS,
      stream: !!useStream,
    };

    if (useStream) {
      const controller = new AbortController();
      abortRef.current = controller;
      const resp = await apiFetch(endpoint, {
        method: "POST",
        headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
        body,
        raw: true,
        signal: controller.signal,
      });
      if (resp.status === 401) {
        localStorage.removeItem("token");
        const msg = (await resp.text().catch(() => "")) || "未登录或登录已过期，请重新登录";
        const err = new Error(msg); err.status = 401; err.displayMsg = msg; throw err;
      }
      if (!resp.ok) {
        const displayMsg = await extractServerMessage(resp);
        const err = new Error(`接口错误 ${resp.status}`); err.status = resp.status; err.displayMsg = displayMsg; throw err;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const raw of lines) {
          const line = raw.trim();
          if (!line || line.startsWith(":") || !line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const json = JSON.parse(payload);
            const delta =
              json?.choices?.[0]?.delta?.content ??
              json?.choices?.[0]?.message?.content ??
              json?.content ?? "";
            if (delta && onStreamChunk) onStreamChunk(delta);
          } catch {}
        }
      }
      abortRef.current = null;
      return;
    } else {
      const resp = await apiFetch(endpoint, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body,
        raw: true,
      });
      if (resp.status === 401) {
        localStorage.removeItem("token");
        const msg = (await resp.text().catch(() => "")) || "未登录或登录已过期，请重新登录";
        const err = new Error(msg); err.status = 401; err.displayMsg = msg; throw err;
      }
      if (!resp.ok) {
        const displayMsg = await extractServerMessage(resp);
        const err = new Error(`接口错误 ${resp.status}`); err.status = resp.status; err.displayMsg = displayMsg; throw err;
      }
      const ct = resp.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const data = await resp.json();
        return data?.choices?.[0]?.message?.content ?? data?.choices?.[0]?.delta?.content ?? "";
      }
      return await resp.text();
    }
  };

  /** ---------------- 发送/停止/清空 ---------------- */
  const handleSend = async () => {
    setError("");
    const content = input.trim();
    if (!content || loading) return;
    if (!initReady || !model) {
      setError(initError ? `初始化失败：${initError}` : "通道未就绪，请稍后重试。");
      return;
    }
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    setActiveChip(-1);

    try {
      if (useStream) {
        let acc = "";
        setMessages((prev) => [...prev, { role: "assistant", content: "" }]);
        await call302(next, (chunk) => {
          acc += chunk;
          setMessages((prev) => {
            const cp = prev.slice();
            cp[cp.length - 1] = { role: "assistant", content: acc };
            return cp;
          });
        });
      } else {
        const reply = await call302(next);
        setMessages((prev) => [...prev, { role: "assistant", content: reply || "（空响应）" }]);
      }
    } catch (e) {
      const display = e?.displayMsg?.trim?.() || e?.message || "调用失败";
      setError(display);
      setMessages((prev) => [...prev, { role: "assistant", content: display }]);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
      abortRef.current = null;
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([{ role: "system", content: systemPrompt }]);
    setError("");
    setInput("");
    localStorage.removeItem("pyq_chat_messages_v2");
    setActiveBase(-1);
    setActiveChip(-1);
  };

  /** ---------------- 渲染 ---------------- */
  return (
    <div className="pyq-chat-wrap">
      <header className="pyq-chat-header">
        {!isMobile && (
          <div className="title">
            <Link className="pyq-back" to="/">← 返回</Link>
            <span>朋友圈文案助手</span>
          </div>
        )}
        <div className="ops">
          <span className="meta">
            {initLoading ? (
              <em>通道初始化中…</em>
            ) : initError ? (
              <em style={{ color: "#f2453d" }}>初始化失败：{initError}</em>
            ) : (
              <>模型：<code>{model || "(未就绪)"}</code></>
            )}
          </span>
          <button className="ghost mobile-hidden" onClick={clearChat} disabled={loading}>
            新建会话
          </button>
          <button
            className="ghost"
            onClick={() => setInputCollapsed((v) => !v)}
            aria-pressed={inputCollapsed}
            title={inputCollapsed ? "展开输入区" : "阅读模式（折叠输入区）"}
          >
            {inputCollapsed ? "展开输入" : "阅读模式"}
          </button>
        </div>
      </header>

      <div className="pyq-chat-main" ref={scrollRef}>
        {messages.filter((m) => m.role !== "system").map((m, i) => (
          <div key={i} className={`msg-row ${m.role}`}>
            <div className="avatar">{m.role === "user" ? "🧑" : "🤖"}</div>
            <div className="bubble" dangerouslySetInnerHTML={{ __html: fmtHtml(m.content) }} />
          </div>
        ))}
        {error && <div className="error-tip">{error}</div>}
      </div>

      {/* 输入区：可折叠；不固定，随页面滚动 */}
      {!inputCollapsed && (
        <footer className="pyq-chat-input input-panel">
          {/* ✅ 快捷区（保留折叠逻辑） */}
          <div className="pyq-shortcuts">
            <div className={`pyq-shortcuts-content ${shortcutsCollapsed ? "is-collapsed" : ""}`}>
              <div className="quickbar">
                {quickPrompts.map((t, idx) => (
                  <button
                    key={idx}
                    onClick={() => onQuickSelect(idx)}
                    disabled={loading}
                    className={`chip ${activeBase === idx ? "active" : ""}`}
                    title="点击将覆盖输入框（互斥）"
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="chipbar">
                {chipPrompts.map((c, i) => (
                  <button
                    key={i}
                    className={`chip ${activeChip === i ? "active" : ""}`}
                    onClick={() => onChipClick(i)}
                    disabled={loading}
                    title="点击把该提示添加到输入末尾（不可叠加）"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="shortcuts-toggle">
              <button
                className="toggle-btn"
                onClick={() => setShortcutsCollapsed((v) => !v)}
                aria-expanded={!shortcutsCollapsed}
                title={shortcutsCollapsed ? "展开更多快捷提示" : "收起快捷提示"}
              >
                {shortcutsCollapsed ? "展开更多 ↓" : "收起 ↑"}
              </button>
            </div>
          </div>

          <textarea
            rows={6}
            placeholder="输入内容或先选择『场景开头』，也可以添加一个『快捷提示』"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={loading}
          />

          {/* 发送/停止：统一放在输入框下端 */}
          <div className="actions">
            {useStream && loading ? (
              <button className="stop-btn" onClick={handleStop} disabled={!loading}>
                停止
              </button>
            ) : (
              <button
                className="send-btn"
                onClick={handleSend}
                disabled={loading || !input.trim() || !initReady || !model}
              >
                {loading ? "生成中..." : initReady ? "发送" : "初始化中…"}
              </button>
            )}
          </div>
        </footer>
      )}

      {/* 折叠时的悬浮展开按钮 */}
      {inputCollapsed && (
        <button className="fab-toggle" onClick={() => setInputCollapsed(false)} title="展开输入区">
          展开输入
        </button>
      )}

      {!apiKey && <div className="apikey-tip">⚠️ 未检测到 302 API Key</div>}
    </div>
  );
}
